<template>
    <CreateEditData 
        :model="'artist'"
        :form="form"
        :similarData="similarArtists"
        :loading="loading"
        backTo="/artist"
        @fetchData="getArtist">
    <template #headerActions>
        <div class="flex items-center gap-2">
            <Tooltip :text="$t('form.delete')">
                <Button @click="confirmDelete" type="danger" :disabled="!artistId" class="!m-0 !p-2 flex items-center justify-center">
                    <TrashIcon class="w-5 h-5" />
                </Button>
            </Tooltip>
            <Tooltip :text="$t('form.reset')">
                <Button @click="resetForm" type="secondary" class="!m-0 !p-2 flex items-center justify-center">
                    <ArrowPathIcon class="w-5 h-5 text-gray-500" />
                </Button>
            </Tooltip>
            <Tooltip :text="$t('tour.startTour')">
                <Button @click="() => startArtistFormTour(true)" type="secondary" class="!m-0 !p-2 flex items-center justify-center text-indigo-500">
                    <QuestionMarkCircleIcon class="w-5 h-5" />
                </Button>
            </Tooltip>
            <Button data-tour="artist-save-btn" @click="handleSubmit" type="clear" :disabled="!validateForm()" class="!m-0">
                {{ $t('form.save') }}
            </Button>
        </div>
    </template>
            <form @submit.prevent="handleSubmit" class="">
                <!-- Title -->
                <FormContainer data-tour="artist-name">
                    <Label :label="'Title'" />
                    <Input
                        id="name"
                        v-model="form.name"
                        type="text"
                        required
                        placeholder="Enter song title"
                        @input="searchSimilarArtists"
                    />
                </FormContainer>
                <FormContainer data-tour="artist-bio">
                    <Label :label="'Bio'" />
                    <TextArea
                        id="name"
                        v-model="form.bio"
                        type="text"
                        placeholder="Write the artist's bio"
                        class="h-[200px]"
                    />
                </FormContainer>
                <FormContainer data-tour="artist-tags">
                    <div class="flex items-center gap-2">
                        <Label :label="'Tags'" class="mb-0" />
                        <AIGenerateButton 
                          :loading="isGeneratingTags" 
                          @click="generateTagsAI" 
                        />
                    </div>
                    <TagInput
                        v-model="form.tag"
                        :available-tags="tag"
                        :allow-new="true"
                        :items="tag"
                        placeholder="Add tags"
                        @search="searchTag"
                    />
                </FormContainer>

                <div class="mb-2 mt-2">
                  <MusicBrainzMetadata 
                      :artist-name="form.name"
                      :show-section="artistId"
                      :auto-search="true"
                      @data-loaded="onMusicBrainzDataLoaded"
                      @data-error="onMusicBrainzDataError"
                  />
                </div>

            </form>
    </CreateEditData>
</template>
<script setup>
import { reactive, ref, onMounted } from 'vue';
import toastStore from '@/store/toast.store'
import userStore, { verifyIam } from '@/store/user.store'
import dialogStore from '@/store/dialog.store'
import {debounce} from '@/service/debounce'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { TrashIcon, ArrowPathIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import MusicBrainzMetadata from '@/components/artist/MusicBrainzMetadata.vue'
import TagInput from '@/components/TagInput.vue'
import AIGenerateButton from '@/components/common/elements/AIGenerateButton.vue'
import { useTour } from '@/composables/useTour'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { startArtistFormTour } = useTour()
const artistId = route.params.id
const loading = ref(false)
const similarArtists = ref([])
const isGeneratingTags = ref(false)

onMounted(() => {
  if (artistId) {
    getArtist()
  }
})

const tag = ref([])

const form = reactive({
  name: '',
  bio: '',
  tag: []
})

const handleSubmit = async () => {
  try {
    if (!validateForm()) {
      toastStore.dispatch('addMessage', {
        message: 'Please fill in all fields',
        icon: 'error',
        ttl: 3000
      })
      return;
    }
    let response
    if (artistId) {
      response = await fetch('/api/artist/'+artistId, {
        method: 'PUT',
        body: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json'
        }
      })
    } else{
      response = await fetch('/api/artist/', {
        method: 'POST',
        body: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
    
    if (response.status === 200) {
        toastStore.dispatch('addMessage', {
            message: 'Artist saved',
            icon: 'success',
            ttl: 3000
        })
        router.push('/artist')
    } else {
        toastStore.dispatch('addMessage', {
            message: 'Error saving artist',
            icon: 'error',
            ttl: 3000
        })
    }
  } catch (error) {
    console.error('Error saving artist:', error)
  }
}

const generateTagsAI = async () => {
  if (!form.name && !form.bio) {
    toastStore.dispatch('addMessage', {
      message: 'Name or bio needed for tag generation.',
      icon: 'warning',
      ttl: 3000
    });
    return;
  }
  
  isGeneratingTags.value = true;
  try {
    const payload = {
      type: 'artist',
      name: form.name,
      bio: form.bio
    };
    
    const response = await fetch('/api/ai/generate-tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      const data = await response.json();
      if (data.tags && Array.isArray(data.tags)) {
        // add avoiding duplicates
        const currentTagNames = form.tag.map(t => t.name.toLowerCase());
        const newTags = data.tags.filter(t => !currentTagNames.includes(t.name.toLowerCase()));
        
        form.tag = [...form.tag, ...newTags];
        
        toastStore.dispatch('addMessage', {
          message: 'Tags generated successfully!',
          icon: 'success',
          ttl: 3000
        });
      }
    } else {
      throw new Error('Failed to generate tags');
    }
  } catch (error) {
    console.error('Error generating tags:', error);
    toastStore.dispatch('addMessage', {
      message: 'Failed to generate tags',
      icon: 'error',
      ttl: 3000
    });
  } finally {
    isGeneratingTags.value = false;
  }
}

const confirmDelete = async () => {
    if (await dialogStore.dispatch('confirm', t('form.deleteConfirm'))) {
        try {
            const response = await fetch(`/api/artist/${artistId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                toastStore.dispatch('addMessage', { message: 'Artist deleted', icon: 'success' });
                router.push('/artist');
            } else {
                toastStore.dispatch('addMessage', { message: 'Error deleting artist', icon: 'error' });
            }
        } catch (error) {
            console.error('Error deleting artist:', error);
            toastStore.dispatch('addMessage', { message: 'Error deleting artist', icon: 'error' });
        }
    }
}

const validateForm = () => {
  return form.name
}

const resetForm = () => {
  if (artistId){
    getArtist()
  } else {
    form.name = ''
    form.bio = ''
    form.tag = []

    toastStore.dispatch('addMessage', {
      message: 'Form reseted',
      icon: 'success',
      ttl: 3000
    })
  }
}

const searchSimilarArtists = debounce(async (ev) => {
  if(artistId) {
    return;
  }
  toastStore.dispatch('addMessage', {
      message: 'Searching for similar artist',
      icon: 'info',
      ttl: 1000
  })
  const response = await fetch(`/api/artist/search?q=${ev.target.value.replace(/[\r\n]+/g, ' ')}`);
  if (response.status === 200) {
    const data = await response.json()
    similarArtists.value = data.map( (s) => {
        return {
            ...s,
            name: s.name
        }
    })
    if (similarArtists.value.length > 0) {
        toastStore.dispatch('addMessage', {
            message: similarArtists.value.length + ' similar artists found',
            icon: 'success',
            ttl: 5000
        })
    }
  } else {
    similarArtists.value = [];
    toastStore.dispatch('addMessage', {
      message: 'Error searching for artists songs',
      icon: 'error',
      ttl: 5000
    })
  }
}, 1000)

const getArtist = async () => {
  loading.value = true;
  
  try {
    const response = await fetch(`/api/artist/${artistId}`)
    const data = await response.json();

    // Owner-only editing (admins can manage all): non-owners are sent back.
    const canManageAll = verifyIam(userStore.state.userInfo, '/artist');
    if (data && data.owner === false && !canManageAll) {
      toastStore.dispatch('addMessage', {
        message: t('artistInfo.notOwnerAction'),
        icon: 'warning',
        ttl: 3000
      });
      router.push('/artist');
      return;
    }

    form.name = data.name
    form.bio = data.bio;
    tag.value = [];
    if (data.tags) {
      data.tags.forEach(t => {
        if (typeof t === 'object' && t !== null) {
          tag.value.push({...t})
        } else {
          tag.value.push({name: t})
        }
      })
    }
    form.tag = tag.value;
  } catch (error) {
    console.error('Error loading artist:', error);
    toastStore.dispatch('addMessage', {
      message: 'Error loading artist data',
      icon: 'error',
      ttl: 3000
    });
  } finally {
    loading.value = false;
  }
}

const searchTag = async (query) => {
  const response = await fetch(`/api/tags/search?q=${query}`);
  if (response.status === 200) {
    const data = await response.json()
    tag.value = data
  }
}

// MusicBrainz event handlers
const onMusicBrainzDataLoaded = (data) => {
  console.log('MusicBrainz data loaded:', data);
};

const onMusicBrainzDataError = (error) => {
  console.error('MusicBrainz data error:', error);
};
</script>